<script setup>
import { reactive, computed, watch, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';

import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const captainConfigStore = useCaptainConfigStore();

const PROVIDER_ICONS = {
  openai: 'i-ri-openai-fill',
  anthropic: 'i-ri-anthropic-line',
  mistral: 'i-logos-mistral-icon',
  gemini: 'i-woot-gemini',
};

const MODEL_FEATURES = [
  {
    key: 'editor',
    stateKey: 'editorModel',
    reasoningKey: 'editorReasoningEffort',
    priorityKey: 'editorPriority',
  },
  {
    key: 'assistant',
    stateKey: 'assistantModel',
    reasoningKey: 'assistantReasoningEffort',
    priorityKey: 'assistantPriority',
  },
  {
    key: 'copilot',
    stateKey: 'copilotModel',
    reasoningKey: 'copilotReasoningEffort',
    priorityKey: 'copilotPriority',
  },
];

// Optional inference tuning. Empty value means "use the model/provider default"
// and is never sent to the API, so models without these capabilities keep
// working unchanged.
const REASONING_EFFORT_KEYS = [
  'default',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
];
const PRIORITY_KEYS = ['default', 'auto', 'flex', 'priority'];

const isCaptainV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);

const initialState = {
  handoffMessage: '',
  resolutionMessage: '',
  instructions: '',
  temperature: 1,
  editorModel: '',
  assistantModel: '',
  copilotModel: '',
  editorReasoningEffort: '',
  editorPriority: '',
  assistantReasoningEffort: '',
  assistantPriority: '',
  copilotReasoningEffort: '',
  copilotPriority: '',
  imageModel: '',
  apiKey: '',
  apiEndpoint: '',
  embeddingModel: '',
  firecrawlApiKey: '',
};

const state = reactive({ ...initialState });

const validationRules = {
  handoffMessage: { minLength: minLength(1) },
  resolutionMessage: { minLength: minLength(1) },
  instructions: { minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  handoffMessage: getErrorMessage('handoffMessage'),
  resolutionMessage: getErrorMessage('resolutionMessage'),
  instructions: getErrorMessage('instructions'),
}));

// Model dropdown state
const openDropdown = ref(null);
const modelSearch = reactive({
  editor: '',
  assistant: '',
  copilot: '',
});

// Available models from API
const apiModels = ref([]);
const apiEndpointName = ref('');
const apiModelsFetching = ref(false);
const apiModelsFetched = ref(false);
const apiModelsError = ref('');
const apiModelsFetchedAt = ref(null);

const mapApiModelToOption = model => ({
  id: model.id,
  display_name: model.display_name || model.id,
  provider: model.owned_by || 'openai',
  owned_by: model.owned_by || '',
  credit_multiplier: model.multiplier ?? null,
  description: model.multiplier_label || model.owned_by || '',
  coming_soon: false,
});

const getAvailableModelsForFeature = featureKey => {
  if (apiModelsFetched.value && apiModels.value.length > 0) {
    return apiModels.value.map(mapApiModelToOption);
  }

  return getModelsForFeature(featureKey);
};

const getFilteredModelsForFeature = featureKey => {
  const searchTerm = modelSearch[featureKey]?.trim().toLowerCase();
  const models = getAvailableModelsForFeature(featureKey);

  if (!searchTerm) return models;

  return models.filter(model => {
    const haystack = [
      model.id,
      model.display_name,
      model.provider,
      model.owned_by,
      model.description,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(searchTerm);
  });
};

// Treats empty and 'default' as "no override" so the model/provider default is
// used and nothing extra is sent to the API.
const normalizeInferenceValue = value =>
  value && value !== 'default' ? value : null;

// Static i18n labels (no dynamic keys) for the inference tuning selects.
// The value '' means "global default" and is never sent to the API.
const reasoningEffortLabels = computed(() => ({
  default: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.DEFAULT'),
  low: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.LOW'),
  medium: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.MEDIUM'),
  high: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.HIGH'),
  xhigh: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.XHIGH'),
  max: t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.OPTIONS.MAX'),
}));
const priorityLabels = computed(() => ({
  default: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PRIORITY.OPTIONS.DEFAULT'),
  auto: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PRIORITY.OPTIONS.AUTO'),
  flex: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PRIORITY.OPTIONS.FLEX'),
  priority: t('CAPTAIN_SETTINGS.MODEL_CONFIG.PRIORITY.OPTIONS.PRIORITY'),
}));
const reasoningEffortOptions = computed(() =>
  REASONING_EFFORT_KEYS.map(key => ({
    value: key === 'default' ? '' : key,
    label: reasoningEffortLabels.value[key],
  }))
);
const priorityOptions = computed(() =>
  PRIORITY_KEYS.map(key => ({
    value: key === 'default' ? '' : key,
    label: priorityLabels.value[key],
  }))
);

const getModelsForFeature = featureKey =>
  captainConfigStore.getModelsForFeature(featureKey);

const getDefaultModelForFeature = featureKey =>
  captainConfigStore.getDefaultModelForFeature(featureKey);

const getSelectedModelDetails = (featureKey, stateKey) => {
  const modelId = state[stateKey];
  if (!modelId) return null;
  return (
    getAvailableModelsForFeature(featureKey).find(m => m.id === modelId) || {
      id: modelId,
      display_name: modelId,
      provider: 'openai',
      owned_by: '',
      description: '',
      coming_soon: false,
    }
  );
};

const toggleDropdown = key => {
  openDropdown.value = openDropdown.value === key ? null : key;
  if (openDropdown.value === key) {
    modelSearch[key] = '';
  }
};

const closeAllDropdowns = () => {
  openDropdown.value = null;
};

const selectModel = (stateKey, modelId) => {
  state[stateKey] = modelId;
  openDropdown.value = null;
};

const clearModel = stateKey => {
  state[stateKey] = '';
};

const clearAvailableModelsCache = () => {
  apiModels.value = [];
  apiModelsFetched.value = false;
  apiModelsFetchedAt.value = null;
  apiEndpointName.value = '';
};

const fetchAvailableModels = async () => {
  if (!props.assistant?.id) return;
  apiModelsFetching.value = true;
  apiModelsError.value = '';
  try {
    const { data } = await CaptainAssistantAPI.availableModels({
      assistantId: props.assistant.id,
    });
    apiModels.value = data.models || [];
    apiEndpointName.value = data.endpoint || '';
    apiModelsFetched.value = true;
    apiModelsFetchedAt.value = data.fetched_at;

    const firstModelId = apiModels.value[0]?.id;
    MODEL_FEATURES.forEach(({ stateKey }) => {
      const currentValue = state[stateKey];
      const modelExists = apiModels.value.some(
        model => model.id === currentValue
      );

      if (!currentValue || !modelExists) {
        state[stateKey] = firstModelId || currentValue || '';
      }
    });
  } catch (error) {
    apiModelsError.value =
      error?.response?.data?.error || 'Failed to fetch models';
  } finally {
    apiModelsFetching.value = false;
  }
};

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.handoffMessage = config.handoff_message || '';
  state.resolutionMessage = config.resolution_message || '';
  state.instructions = config.instructions || '';
  state.temperature = config.temperature || 1;
  state.editorModel = config.editor_model || '';
  state.assistantModel = config.assistant_model || '';
  state.copilotModel = config.copilot_model || '';
  state.editorReasoningEffort = config.editor_reasoning_effort || '';
  state.editorPriority = config.editor_priority || '';
  state.assistantReasoningEffort = config.assistant_reasoning_effort || '';
  state.assistantPriority = config.assistant_priority || '';
  state.copilotReasoningEffort = config.copilot_reasoning_effort || '';
  state.copilotPriority = config.copilot_priority || '';
  state.imageModel = config.image_model || '';
  state.apiKey = config.api_key || '';
  state.apiEndpoint = config.api_endpoint || '';
  state.embeddingModel = config.embedding_model || '';
  state.firecrawlApiKey = config.firecrawl_api_key || '';

  apiModels.value = config.available_models_cache || [];
  apiModelsFetched.value = apiModels.value.length > 0;
  apiModelsFetchedAt.value = config.available_models_fetched_at || null;
};

const handleSystemMessagesUpdate = async () => {
  const validations = [
    v$.value.handoffMessage.$validate(),
    v$.value.resolutionMessage.$validate(),
  ];

  if (!isCaptainV2Enabled.value) {
    validations.push(v$.value.instructions.$validate());
  }

  const result = await Promise.all(validations).then(results =>
    results.every(Boolean)
  );
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      handoff_message: state.handoffMessage,
      resolution_message: state.resolutionMessage,
      temperature: state.temperature || 1,
      editor_model: state.editorModel || null,
      assistant_model: state.assistantModel || null,
      copilot_model: state.copilotModel || null,
      editor_reasoning_effort: normalizeInferenceValue(
        state.editorReasoningEffort
      ),
      editor_priority: normalizeInferenceValue(state.editorPriority),
      assistant_reasoning_effort: normalizeInferenceValue(
        state.assistantReasoningEffort
      ),
      assistant_priority: normalizeInferenceValue(state.assistantPriority),
      copilot_reasoning_effort: normalizeInferenceValue(
        state.copilotReasoningEffort
      ),
      copilot_priority: normalizeInferenceValue(state.copilotPriority),
      image_model: state.imageModel || null,
      api_key: state.apiKey || null,
      api_endpoint: state.apiEndpoint || null,
      embedding_model: state.embeddingModel || null,
      firecrawl_api_key: state.firecrawlApiKey || null,
      available_models_cache: apiModels.value,
      available_models_fetched_at: apiModelsFetchedAt.value,
    },
  };

  if (!isCaptainV2Enabled.value) {
    payload.config.instructions = state.instructions;
  }

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);

watch(
  () => [state.apiKey, state.apiEndpoint],
  ([newApiKey, newApiEndpoint], [oldApiKey, oldApiEndpoint]) => {
    if (newApiKey === oldApiKey && newApiEndpoint === oldApiEndpoint) return;
    clearAvailableModelsCache();
  }
);

onMounted(() => {
  captainConfigStore.fetch();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <Editor
      v-model="state.handoffMessage"
      :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')"
      :message="formErrors.handoffMessage"
      :message-type="formErrors.handoffMessage ? 'error' : 'info'"
      class="z-0"
    />

    <Editor
      v-model="state.resolutionMessage"
      :label="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')"
      :message="formErrors.resolutionMessage"
      :message-type="formErrors.resolutionMessage ? 'error' : 'info'"
      class="z-0"
    />

    <Editor
      v-if="!isCaptainV2Enabled"
      v-model="state.instructions"
      :label="t('CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.PLACEHOLDER')"
      :message="formErrors.instructions"
      :max-length="20000"
      :message-type="formErrors.instructions ? 'error' : 'info'"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
      </label>
      <div class="flex items-center gap-4">
        <input
          v-model="state.temperature"
          type="range"
          min="0"
          max="1"
          step="0.1"
          class="w-full"
        />
        <span class="text-sm text-n-slate-12">{{ state.temperature }}</span>
      </div>
      <p class="text-sm text-n-slate-11 italic">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
      </p>
    </div>

    <!-- Model Configuration Section -->
    <div class="flex flex-col gap-4 border-t border-n-weak pt-6">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.MODEL_CONFIG.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('CAPTAIN.ASSISTANTS.FORM.MODEL_CONFIG.DESCRIPTION') }}
        </p>
      </div>

      <div v-on-clickaway="closeAllDropdowns" class="grid gap-4">
        <div
          v-for="feature in MODEL_FEATURES"
          :key="feature.key"
          class="flex flex-col gap-1"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    `CAPTAIN_SETTINGS.MODEL_CONFIG.${feature.key.toUpperCase()}.TITLE`
                  )
                }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{
                  t(
                    `CAPTAIN_SETTINGS.MODEL_CONFIG.${feature.key.toUpperCase()}.DESCRIPTION`
                  )
                }}
              </p>
            </div>
            <div class="relative flex-shrink-0">
              <button
                type="button"
                class="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg border-n-weak dark:bg-n-solid-2 dark:hover:bg-n-solid-3 bg-n-alpha-2 hover:bg-n-alpha-1 min-w-[180px] justify-between"
                @click="toggleDropdown(feature.key)"
              >
                <span
                  v-if="getSelectedModelDetails(feature.key, feature.stateKey)"
                  class="text-n-slate-12 truncate"
                >
                  {{
                    getSelectedModelDetails(feature.key, feature.stateKey)
                      .display_name
                  }}
                </span>
                <span v-else class="text-n-slate-10">
                  {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.SELECT_MODEL') }}
                </span>
                <Icon
                  icon="i-lucide-chevron-down"
                  class="size-4 text-n-slate-11 transition-transform"
                  :class="{ 'rotate-180': openDropdown === feature.key }"
                />
              </button>
              <div
                v-if="openDropdown === feature.key"
                class="absolute right-0 top-full mt-1 min-w-64 z-50 max-h-96 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-lg"
              >
                <div
                  class="sticky top-0 z-10 bg-n-solid-2 p-2 border-b border-n-weak"
                >
                  <Input
                    v-model="modelSearch[feature.key]"
                    :placeholder="
                      t('CAPTAIN_SETTINGS.MODEL_CONFIG.SELECT_MODEL')
                    "
                  />
                </div>
                <button
                  v-for="model in getFilteredModelsForFeature(feature.key)"
                  :key="model.id"
                  type="button"
                  class="flex gap-2 w-full px-3 py-2 rounded-lg text-left"
                  :class="{
                    'dark:bg-n-solid-3 bg-n-alpha-1':
                      state[feature.stateKey] === model.id,
                    'hover:bg-n-alpha-1 dark:hover:bg-n-solid-3':
                      state[feature.stateKey] !== model.id,
                    'pointer-events-none opacity-60': model.coming_soon,
                  }"
                  @click="selectModel(feature.stateKey, model.id)"
                >
                  <Icon
                    :icon="PROVIDER_ICONS[model.provider] || 'i-lucide-bot'"
                    class="size-4 flex-shrink-0 mt-0.5"
                  />
                  <div class="flex flex-col w-full gap-1">
                    <div
                      class="text-sm font-medium leading-none text-n-slate-12 flex items-baseline justify-between"
                    >
                      {{ model.display_name }}
                      <span
                        v-if="
                          model.id === getDefaultModelForFeature(feature.key)
                        "
                        class="text-[10px] uppercase text-n-iris-11 border border-1 border-n-iris-10 leading-none rounded-lg px-1 py-0.5"
                      >
                        {{ t('GENERAL.PREFERRED') }}
                      </span>
                    </div>
                    <span
                      v-if="model.coming_soon"
                      class="text-xs text-n-slate-11"
                    >
                      {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.COMING_SOON') }}
                    </span>
                  </div>
                </button>
                <p
                  v-if="getFilteredModelsForFeature(feature.key).length === 0"
                  class="px-3 py-4 text-sm text-n-slate-11"
                >
                  Nenhum modelo encontrado.
                </p>
              </div>
            </div>
          </div>
          <button
            v-if="state[feature.stateKey]"
            type="button"
            class="text-xs text-n-slate-11 hover:text-n-slate-12 underline self-end"
            @click="clearModel(feature.stateKey)"
          >
            {{ t('CAPTAIN.ASSISTANTS.FORM.MODEL.USE_DEFAULT') }}
          </button>

          <!-- Optional inference tuning per feature block -->
          <div class="grid grid-cols-2 gap-3 mt-1">
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.REASONING_EFFORT.LABEL') }}
              </span>
              <select
                v-model="state[feature.reasoningKey]"
                class="rounded-lg border border-n-weak bg-n-alpha-black1 dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
              >
                <option
                  v-for="option in reasoningEffortOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('CAPTAIN_SETTINGS.MODEL_CONFIG.PRIORITY.LABEL') }}
              </span>
              <select
                v-model="state[feature.priorityKey]"
                class="rounded-lg border border-n-weak bg-n-alpha-black1 dark:bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
              >
                <option
                  v-for="option in priorityOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Models Query -->
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3">
        <button
          type="button"
          class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak hover:bg-n-alpha-1 dark:hover:bg-n-solid-3 transition-colors"
          :class="{ 'opacity-60 pointer-events-none': apiModelsFetching }"
          @click="fetchAvailableModels"
        >
          <Icon
            icon="i-lucide-globe"
            class="size-4"
            :class="{ 'animate-spin': apiModelsFetching }"
          />
          {{
            apiModelsFetching
              ? t('CAPTAIN.ASSISTANTS.FORM.AVAILABLE_MODELS.LOADING')
              : t('CAPTAIN.ASSISTANTS.FORM.AVAILABLE_MODELS.BUTTON')
          }}
        </button>
        <span v-if="apiEndpointName" class="text-xs text-n-slate-10 font-mono">
          {{ apiEndpointName }}
        </span>
      </div>

      <p v-if="apiModelsError" class="text-sm text-n-ruby-9">
        {{ apiModelsError }}
      </p>

      <p
        v-if="apiModelsFetched && apiModels.length"
        class="text-xs text-n-slate-11"
      >
        {{
          t('CAPTAIN.ASSISTANTS.FORM.AVAILABLE_MODELS.COUNT', {
            count: apiModels.length,
          })
        }}
      </p>
    </div>
    <div class="flex flex-col gap-4 border-t border-n-weak pt-6">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.DESCRIPTION') }}
        </p>
      </div>

      <Input
        v-model="state.apiKey"
        type="password"
        :label="t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.API_KEY.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.API_KEY.PLACEHOLDER')
        "
      />

      <Input
        v-model="state.apiEndpoint"
        :label="t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.API_ENDPOINT.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.API_ENDPOINT.PLACEHOLDER')
        "
      />

      <Input
        v-model="state.embeddingModel"
        :label="t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.EMBEDDING_MODEL.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.EMBEDDING_MODEL.PLACEHOLDER')
        "
      />

      <Input
        v-model="state.imageModel"
        :label="t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.IMAGE_MODEL.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.IMAGE_MODEL.PLACEHOLDER')
        "
      />

      <Input
        v-model="state.firecrawlApiKey"
        type="password"
        :label="t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.FIRECRAWL_API_KEY.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.API_CONFIG.FIRECRAWL_API_KEY.PLACEHOLDER')
        "
      />
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSystemMessagesUpdate"
      />
    </div>
  </div>
</template>
